import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useSelector } from "react-redux";
import { getIdModalState } from "@/entities/idModal";
import { useFetchAppReplaysMutation } from "@/features/auth/api/authAPI";
import { AppResponseData, SPageResponse } from "@/features/auth/types/auth";
import { ObserverWrapper } from "@/widgets/observerWrapper/ui/ObserverWrapper/ObserverWrapper";
import { useCurrentOrganization } from "@/Hooks";
import { useWindowWidth } from "@/Hooks/useWindowWidth";
import { useInView } from "react-intersection-observer";
import { VariableSizeList as List } from "react-window";
import InfiniteLoader from "react-window-infinite-loader";
import AutoSizer from "react-virtualized-auto-sizer";
import ExploreReplayRow from "@/Views/Virtual/ExploreReplayRow";
import SelectedScreensPopup from "@/Views/Virtual/SelectedScreensPopup/SelectedScreensPopup";
import { useSelectedScreens } from "@/entities/selectedScreens";
import Loader from "@/shared/ui/Loader/Loader";
import { formatNumber } from "@/widgets/phoneHomePageHorisontal/libs/const/formatNumber";
import styles from "./SPagesApps.module.scss";

interface SPagesAppsProps {
  sPage: SPageResponse;
}

interface RowProps {
  index: number;
  style: React.CSSProperties;
}

interface ListRef {
  resetAfterIndex: (index: number) => void;
}

const SPagesApps: React.FC<SPagesAppsProps> = ({ sPage }) => {
  const { openModalId } = useSelector(getIdModalState);
  const currentOrganization = useCurrentOrganization();
  const modalRef = useRef<HTMLDivElement>(null);
  const windowWidth = useWindowWidth();

  const [fetchAppReplays, { isLoading }] = useFetchAppReplaysMutation();
  const [appReplays, setAppReplays] = useState<AppResponseData | null>(null);
  const [page, setPage] = useState(1);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const pageSize = 10;

  // Use ref to store current data for Column component access
  const appReplaysRef = useRef(appReplays);
  appReplaysRef.current = appReplays;

  useEffect(() => {
    if (sPage.id) {
      // Reset state when spage ID changes
      setAppReplays(null);
      setPage(1);

      fetchAppReplays({
        id: 1,
        spage: sPage.id,
      }).then((res) => {
        if (res.data) {
          setAppReplays(res.data);
        }
      });
    }
  }, [fetchAppReplays, sPage.id]);

  const loadMoreApps = useCallback(async () => {
    if (!sPage.id || isFetchingMore || !appReplays?.next) {
      return Promise.resolve();
    }

    setIsFetchingMore(true);
    const nextPage = page + 1;

    try {
      const response = await fetchAppReplays({
        id: nextPage,
        spage: sPage.id,
      });

      if (response.data) {
        setAppReplays((prevData) => {
          if (!prevData) return response.data;

          return {
            ...response.data,
            results: [...prevData.results, ...response.data.results],
          };
        });
        setPage(nextPage);
      }
    } catch (error) {
      console.error("Error loading more apps:", error);
    } finally {
      setIsFetchingMore(false);
    }

    return Promise.resolve();
  }, [sPage.id, page, isFetchingMore, appReplays?.next, fetchAppReplays]);

  // Function to update replay screens saved status
  const updateReplayScreensSavedStatus = useCallback(
    (
      screenIds: number[],
      isSaved: boolean,
      savedPointIds: Record<number, number> | null,
    ) => {
      setAppReplays((prevAppReplays) => {
        if (!prevAppReplays) return prevAppReplays;

        return {
          ...prevAppReplays,
          results: prevAppReplays.results.map((app: any) => {
            // Check if app has replays property (for collection apps) or avs.screens (for regular apps)
            if (app.replays) {
              const updatedReplays = app.replays.map((screen: any) => {
                if (screenIds.includes(screen.id)) {
                  return {
                    ...screen,
                    is_saved: isSaved,
                    is_saved_pk:
                      isSaved && savedPointIds
                        ? savedPointIds[screen.id] || null
                        : null,
                  };
                }
                return screen;
              });

              const hasChanges = updatedReplays.some(
                (screen: any, index: number) =>
                  screen.is_saved !== app.replays[index].is_saved ||
                  screen.is_saved_pk !== app.replays[index].is_saved_pk,
              );

              if (hasChanges) {
                return {
                  ...app,
                  replays: updatedReplays,
                };
              }
            } else if (app.avs?.screens) {
              // Handle regular app data structure
              const updatedScreens = app.avs.screens.map((screen: any) => {
                if (screenIds.includes(screen.id)) {
                  return {
                    ...screen,
                    is_saved: isSaved,
                    is_saved_pk:
                      isSaved && savedPointIds
                        ? savedPointIds[screen.id] || null
                        : null,
                  };
                }
                return screen;
              });

              const hasChanges = updatedScreens.some(
                (screen: any, index: number) =>
                  screen.is_saved !== app.avs.screens[index]?.is_saved ||
                  screen.is_saved_pk !== app.avs.screens[index]?.is_saved_pk,
              );

              if (hasChanges) {
                return {
                  ...app,
                  avs: {
                    ...app.avs,
                    screens: updatedScreens,
                  },
                };
              }
            }

            return app;
          }),
        };
      });
    },
    [setAppReplays],
  );

  const infiniteLoaderRef = useRef(null);
  const listRef = useRef<ListRef | null>(null);

  const isItemLoaded = (index: number) => {
    // Index 0 is always the hero section
    if (index === 0) return true;
    // Adjust for hero section offset
    const appIndex = index - 1;
    const loaded = appIndex < (appReplays?.results.length || 0);
    return loaded;
  };

  const itemCount = Math.max(
    1, // Always at least 1 for hero section
    1 + (appReplays?.results.length || 0) + (appReplays?.next ? 1 : 0),
  );

  // Memoize hero section heights based on window width (following your pattern)
  const heroHeight = useMemo(() => {
    if (windowWidth < 540) return 1000;
    if (windowWidth < 650) return 900;
    if (windowWidth < 968) return 750;
    return 650;
  }, [windowWidth]);

  const isMobile = windowWidth < 640;

  // Memoize app row height based on window width if needed
  const appRowHeight = useMemo(() => {
    // You can add responsive logic here if app rows should also change height
    return isMobile ? 620 : 770;
  }, [windowWidth]);

  const getItemSize = useCallback(
    (index: number) => {
      // Hero section
      if (index === 0) {
        return heroHeight;
      }

      // Loading spinner
      if (!isItemLoaded(index)) {
        return 100;
      }

      // App rows
      return appRowHeight;
    },
    [heroHeight, appRowHeight, isItemLoaded],
  );

  const loadMoreItems = useCallback(
    (startIndex: number, stopIndex: number) => {
      if (isFetchingMore || !appReplays?.next) {
        return Promise.resolve();
      }

      return loadMoreApps();
    },
    [isFetchingMore, appReplays?.next, loadMoreApps],
  );

  const Column = useCallback(
    ({ index, style }: RowProps) => {
      // Hero section as first row
      if (index === 0) {
        return (
          <div style={style} className={styles.heroRow}>
            <div className={styles.hero}>
              <h1>{sPage?.keyword}</h1>
              <p
                className={styles.paragraph}
                dangerouslySetInnerHTML={{
                  __html: sPage?.attrs.paragraph || "",
                }}
              />
              <div className={styles.stats}>
                <div className={styles.stat}>
                  <p>Average Revenue</p>
                  <span>
                    {sPage?.attrs.average_revenue === 0 ||
                    (sPage?.attrs.average_revenue > 0 &&
                      sPage?.attrs.average_revenue < 5000)
                      ? "<5k"
                      : formatNumber(sPage?.attrs.average_revenue)}
                  </span>
                </div>
                <div className={styles.stat}>
                  <p>Average Installs</p>
                  <span>
                    {sPage?.attrs.average_installs === 0 ||
                    (sPage?.attrs.average_installs > 0 &&
                      sPage?.attrs.average_installs < 5000)
                      ? "<5k"
                      : formatNumber(sPage?.attrs.average_installs)}
                  </span>
                </div>
                <div className={styles.stat}>
                  <p>Average Onboarding Steps</p>
                  <span>{sPage?.attrs.average_onboarding_steps}</span>
                </div>
                <div className={styles.stat}>
                  <p>Top App Name</p>
                  <span>{sPage?.attrs.top_app_name}</span>
                </div>
                <div className={styles.stat}>
                  <p>Most Common Paywall Type</p>
                  <span>{sPage?.attrs.most_common_paywall_type}</span>
                </div>
              </div>
            </div>
          </div>
        );
      }

      // Get current data from ref to always have latest data
      const currentAppReplays = appReplaysRef.current?.results || [];
      const appIndex = index - 1; // Adjust for hero section
      const appData = currentAppReplays[appIndex];

      if (!appData) {
        // Show loading spinner for items that haven't loaded yet
        if (!isItemLoaded(index)) {
          return (
            <div
              style={{ ...style, display: "flex", justifyContent: "center" }}
            >
              <Loader size={40} />
            </div>
          );
        }
        // Return null for items that should exist but don't
        return null;
      }

      return (
        <div style={style} key={`spage-${appData.id}-${index}`}>
          <ObserverWrapper index={index} onViewChange={() => {}}>
            <ExploreReplayRow
              key={appData.id}
              isOpen={openModalId === index}
              dataApi={appData}
              setIsPhonePageLoading={() => {}}
              ref={modalRef}
              isFirstGroup={false}
              currentOrganization={currentOrganization}
              isPaywallOnly={false}
              isPhonePageLoading={false}
              rowIndex={index}
              isCollection={false}
            />
          </ObserverWrapper>
        </div>
      );
    },
    // Only include stable dependencies, not the data itself
    [openModalId, currentOrganization, sPage],
  );

  const { selectedScreens } = useSelectedScreens();

  // Add intersection observer for infinite loading in grid view
  const { ref: refIntersection, inView } = useInView({ threshold: 0 });

  useEffect(() => {
    if (inView && appReplays?.next && !isFetchingMore) {
      loadMoreApps();
    }
  }, [inView, appReplays?.next, isFetchingMore, loadMoreApps]);

  // Show full-screen loader only on initial load when there's no data yet
  const showInitialLoader = isLoading && !appReplays;

  if (showInitialLoader) {
    return (
      <div className={styles.loading}>
        <Loader size={40} />
      </div>
    );
  }

  if (!appReplays || appReplays.results.length === 0) {
    return (
      <div className={styles.noMatches}>
        <img
          src="/img/Illustrations.jpg"
          width="250px"
          height="250px"
          alt="no results"
        />
        <h2>No Apps Found</h2>
        <p>
          No apps found for this category.
          <br />
          Try exploring other categories.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <AutoSizer>
        {({ height, width }: { height: number; width: number }) => (
          <InfiniteLoader
            ref={infiniteLoaderRef}
            isItemLoaded={isItemLoaded}
            itemCount={itemCount}
            loadMoreItems={loadMoreItems}
            threshold={1}
            minimumBatchSize={1}
          >
            {({
              onItemsRendered,
              ref,
            }: {
              onItemsRendered: (params: {
                overscanStartIndex: number;
                overscanStopIndex: number;
                visibleStartIndex: number;
                visibleStopIndex: number;
              }) => void;
              ref: (list: any) => void;
            }) => (
              <List
                key={`${heroHeight}-${appRowHeight}-${isMobile}`}
                height={height}
                itemCount={itemCount}
                itemSize={getItemSize}
                onItemsRendered={onItemsRendered}
                ref={(list: any) => {
                  ref(list);
                  listRef.current = list;
                }}
                width={width}
              >
                {Column}
              </List>
            )}
          </InfiniteLoader>
        )}
      </AutoSizer>
      {selectedScreens.length > 0 && (
        <SelectedScreensPopup
          type="appReplays"
          updateReplayScreensSavedStatus={updateReplayScreensSavedStatus}
        />
      )}
    </div>
  );
};

export default SPagesApps;
